import { useMemo, useState } from 'react';
import CopyButton from '../../../components/CopyButton';
import { createApiCall } from '../../../services/apiClient';

interface CustomClaimsResult {
    userId: string;
    email: string | null;
    displayName: string | null;
    disabled: boolean;
    customClaims: Record<string, unknown>;
}

const getCustomClaims = createApiCall<{ userId: string }, CustomClaimsResult>('adminGetCustomClaims');

export const CustomClaimsChecker = () => {
    const [userId, setUserId] = useState('');
    const [result, setResult] = useState<CustomClaimsResult | null>(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const claimsJson = useMemo(
        () => result ? JSON.stringify(result.customClaims, null, 2) : '',
        [result],
    );
    const hasClaims = result ? Object.keys(result.customClaims).length > 0 : false;

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const normalizedUserId = userId.trim();
        if (!normalizedUserId) {
            setResult(null);
            setErrorMessage('Enter a Firebase user ID.');
            return;
        }

        setIsLoading(true);
        setErrorMessage('');
        setResult(null);
        try {
            setResult(await getCustomClaims({ userId: normalizedUserId }));
        } catch (error) {
            console.error('Custom claims check failed', error);
            setErrorMessage(error instanceof Error ? error.message : 'Could not read custom claims.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex flex-wrap items-center gap-2 mb-2">
                <h2 className="text-lg font-semibold text-gray-900">Firebase Custom Claims Checker</h2>
                <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                    Read only
                </span>
            </div>
            <p className="mb-4 text-sm text-gray-600">
                Look up the claims stored on a Firebase Auth user. This tool cannot change claims.
            </p>

            <form className="flex flex-col gap-3 sm:flex-row sm:items-end" onSubmit={handleSubmit}>
                <div className="flex-1">
                    <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="custom-claims-user-id">
                        Firebase user ID
                    </label>
                    <input
                        id="custom-claims-user-id"
                        type="text"
                        value={userId}
                        onChange={(event) => {
                            setUserId(event.target.value);
                            setErrorMessage('');
                            setResult(null);
                        }}
                        placeholder="Enter a UID"
                        autoComplete="off"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                </div>
                <button
                    type="submit"
                    disabled={isLoading}
                    className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {isLoading ? 'Checking...' : 'Check claims'}
                </button>
            </form>

            {errorMessage ? (
                <p className="mt-3 text-sm text-red-600" role="alert">{errorMessage}</p>
            ) : null}

            {result ? (
                <div className="mt-5 rounded-md border border-gray-200 bg-gray-50 p-4">
                    <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0 text-sm">
                            <p className="break-words font-medium text-gray-900">
                                {result.displayName ?? result.email ?? result.userId}
                            </p>
                            {result.email ? <p className="break-words text-gray-600">{result.email}</p> : null}
                            <p className="break-all font-mono text-xs text-gray-500">{result.userId}</p>
                            {result.disabled ? <p className="mt-1 text-xs font-medium text-red-600">Account disabled</p> : null}
                        </div>
                        <CopyButton text={claimsJson} label="Copy JSON" disabled={!hasClaims} />
                    </div>
                    <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-md bg-gray-900 p-4 text-sm text-gray-100">
                        {hasClaims ? claimsJson : 'No custom claims set.'}
                    </pre>
                </div>
            ) : null}
        </div>
    );
};
